/**
 * TASK-827 — a value tag, with what it actually rests on.
 *
 * Every tag in this system is a GICS **industry-group default** (optionally region-qualified).
 * `shared/constants/instrument_tags.py` declares that of itself — *"industry-group defaults
 * are demo-grade; upgrade to a per-issuer ESG/data feed when the universe outgrows
 * industry-group granularity"*. That is a known ceiling, not a coding error.
 *
 * The defect is that the ceiling was invisible at the point of use. Live 2026-08-25, **8 of
 * 11** conflicts on one client carried the identical pair `luxury + labour-risk`, and Tyson
 * Foods (meat processing), PDD (a discount marketplace), Booking, Amazon, Tesla, Albemarle
 * (lithium chemicals) and Sika (construction chemicals) were all reported as breaching
 * *"No luxury conglomerates"*. Ferrari and Adidas — which do fit — are in the same bucket for
 * the same reason and carry no more evidence than Tyson Foods does.
 *
 * "Tyson Foods breaches your no-luxury-conglomerates red line" is the kind of sentence an
 * advisor repeats to a client. It is not defensible on the evidence behind it, and nothing on
 * the surface let the advisor discover that. This chip is what lets them.
 *
 * TASK-837 — and once they can see it, they can correct it. An override is rendered as a
 * DIFFERENT PROVENANCE, never as a silently missing tag: a correction that looked like a
 * machine tag (or like nothing at all) would make the fit score unexplainable six months
 * later, which is the same defect TASK-827 fixed one layer down.
 */

import type { TagProvenance } from '../../services/portfolioFitService';

export function TagProvenanceChip({
  tag,
  provenance,
  className = '',
}: {
  tag: string;
  provenance?: TagProvenance;
  className?: string;
}) {
  // No provenance (older backend) → render the tag exactly as before. Inventing a basis
  // would be a worse overclaim than the one this exists to remove.
  const overridden = provenance?.source === 'advisor_override';
  const title = !provenance
    ? undefined
    : overridden
      ? `${provenance.basis === 'remove' ? 'Removed' : 'Applied'} by ${provenance.actor ?? 'an advisor'}` +
        `${provenance.at ? ` on ${provenance.at.slice(0, 10)}` : ''}` +
        `${provenance.rationale ? ` — ${provenance.rationale}` : ''}`
      : provenance.source === 'region'
        ? `Applied to every holding in ${provenance.basis} — a sector/region default, not a finding about this issuer.`
        : `Applied to every holding in ${provenance.basis} — a sector default, not a finding about this issuer.`;

  return (
    <span
      data-testid="tag-chip"
      data-tag-source={provenance?.source ?? 'unknown'}
      title={title}
      className={
        'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] ' +
        // An advisor's judgment is not the machine's, and it does not wear the machine's
        // colour. The visual difference is the point of AC 6, not decoration.
        (overridden
          ? 'border-foreground/30 bg-accent text-foreground'
          : 'border-destructive/30 bg-destructive/10 text-destructive') +
        ` ${className}`
      }
    >
      {tag}
      {provenance && (
        <span
          data-testid="tag-provenance"
          className={overridden ? 'font-normal text-muted-foreground' : 'font-normal text-destructive/70'}
          // The basis is named, not just hinted at: "sector default" alone still leaves the
          // advisor guessing WHICH sector decided it. For an override the basis is WHO — an
          // unattributed correction is the thing migration 208 was written against.
        >
          · {overridden
            ? `${provenance.basis === 'remove' ? 'removed' : 'added'} by ${provenance.actor ?? 'an advisor'}`
            : provenance.basis}
        </span>
      )}
    </span>
  );
}

export default TagProvenanceChip;
